import { AxiosResponse } from 'axios';
import { api } from './config';
import {
  FuelReceipt, CreateFuelReceipt
} from '../../types';

export const fuelReceiptApi = {
  getAll: (): Promise<AxiosResponse<FuelReceipt[]>> =>
    api.get('/fuelreceipt'),
    
  getById: (id: number): Promise<AxiosResponse<FuelReceipt>> =>
    api.get(`/fuelreceipt/${id}`),
    
  create: (data: CreateFuelReceipt | FormData): Promise<AxiosResponse<FuelReceipt>> => {
    if (data instanceof FormData) {
      return api.post('/fuelreceipt', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post('/fuelreceipt', data);
  },
  
  update: (id: number, data: CreateFuelReceipt | FormData): Promise<AxiosResponse<FuelReceipt>> => {
    if (data instanceof FormData) {
      return api.put(`/fuelreceipt/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.put(`/fuelreceipt/${id}`, data);
  },
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/fuelreceipt/${id}`),

  processFuelReceipt: (file: File): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/fuelreceipt/process-fuel-receipt', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Remove withCredentials: true since it's causing CORS issues
      // The JWT token in Authorization header is sufficient for authentication
      timeout: 600000, // 10 minutes timeout for AI processing
    });
  },

  // New methods for state-based functionality
  getByState: (state: string): Promise<AxiosResponse<FuelReceipt[]>> =>
    api.get(`/fuelreceipt/by-state/${state}`),
    
  updateState: (id: number, state: string): Promise<AxiosResponse<any>> =>
    api.put(`/fuelreceipt/${id}/state`, { state }),

  checkDuplicateReceipt: (receiptNumber: string, excludeId?: number): Promise<AxiosResponse<{
    isDuplicate: boolean;
    existingReceipt?: { id: number; refuelDate: string; gasStation: string; totalAmount: number };
  }>> =>
    api.get(`/fuelreceipt/check-duplicate-receipt/${encodeURIComponent(receiptNumber)}`, {
      params: excludeId ? { excludeId } : undefined
    }),
    
  getReceiptImage: (id: number): string =>
    `${api.defaults.baseURL}/fuelreceipt/${id}/receipt-image`,

  // New file viewing methods
  getReceiptFileInfo: (id: number): Promise<AxiosResponse<{
    hasFile: boolean;
    fileName?: string;
    contentType?: string;
    fileSize?: number;
    fileExists: boolean;
    uploadDate: string;
    viewUrl?: string;
    downloadUrl?: string;
  }>> =>
    api.get(`/fuelreceipt/${id}/receipt-file-info`),
    
  downloadReceiptImage: (id: number): string =>
    `${api.defaults.baseURL}/fuelreceipt/${id}/receipt-image/download`,
    
  deleteReceiptImage: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/fuelreceipt/${id}/receipt-image`),

  // Existing methods for linking functionality
  unlink: (id: number): Promise<AxiosResponse<void>> =>
    api.post(`/fuelreceipt/${id}/unlink`),
    
  getUnlinked: (): Promise<AxiosResponse<FuelReceipt[]>> =>
    api.get('/fuelreceipt/unlinked'),

  getImageBlob: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/fuelreceipt/${id}/receipt-image`, { responseType: 'blob' }),
};

// Shipping Price API
export const fuelEfficiencyApi = {
  // VCOMSAT GPS-based calculation (NEW - no odometer required)
  calculateVcomsat: (
    vehicleId: number, 
    fuelStationLat: number,
    fuelStationLon: number,
    fromDate?: string, 
    toDate?: string,
    radiusKm?: number
  ): Promise<AxiosResponse<any>> =>
    api.post(`/fuelefficiency/calculate-vcomsat/${vehicleId}`, {
      fuelStationLatitude: fuelStationLat,
      fuelStationLongitude: fuelStationLon,
   fromDate,
      toDate,
      radiusKm: radiusKm || 0.06
    }),
  
  // Odometer-based calculation (LEGACY - requires odometer readings)
  calculate: (vehicleId: number, fromDate?: string, toDate?: string): Promise<AxiosResponse<any>> =>
    api.post(`/fuelefficiency/calculate/${vehicleId}`, { fromDate, toDate }),
    
  getHistory: (vehicleId: number): Promise<AxiosResponse<any[]>> =>
    api.get(`/fuelefficiency/history/${vehicleId}`),
 
  getSummary: (vehicleId: number): Promise<AxiosResponse<any>> =>
    api.get(`/fuelefficiency/summary/${vehicleId}`),

  // Get fuel efficiency for all vehicles
  getAllVehiclesHistory: (days: number = 30): Promise<AxiosResponse<any>> =>
    api.get('/fuelefficiency/all-vehicles-history', { params: { days } }),

  // Recalculate fuel efficiency for all vehicles (last N days) using GPS data
  recalculateAllVcomsat: (days: number = 30): Promise<AxiosResponse<any>> =>
    api.post('/fuelefficiency/recalculate-all-vcomsat', null, { params: { days } })
};
